import clsx from 'clsx'
import isEqual from 'lodash/isEqual'

import { Text } from '@wartek-id/text'
import { Separator } from '@wartek-id/separator'
import { Link } from '@wartek-id/link'

import AdjustmentInfo from '../AdjustmentInfo'
import { translateTargetWaktu } from 'app/KinerjaPage/utils/kinerja'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'

import type {
  KinerjaPraktikPembelajaranContentIndikator,
  KinerjaUtamaDetail,
} from 'types/kinerja'
import type { HTMLProps } from 'react'

export const DEFAULT_LINK_GUIDE = PANDUAN_PENGELOLAAN_URL

interface PanelKinerjaProps extends HTMLProps<HTMLDivElement> {
  adjustmentText?: string
  additionalInfo?: string
  number: number | string
  linkGuide?: string
  className?: string
  showChange?: boolean
  showDetails?: boolean
  hideAdjustmentLabel?: boolean
  label?: string

  kinerja?: KinerjaPraktikPembelajaranContentIndikator | KinerjaUtamaDetail
  originalKinerja?:
    | KinerjaPraktikPembelajaranContentIndikator
    | KinerjaUtamaDetail
  originalKinerjaNumber?: string | number
  kinerjaNumber?: string | number

  originalAdjustmentLabel?: string
  reviewAdjustmentLabel?: string
}

const PanelKinerja = ({
  adjustmentText = 'Ada penyesuaian',
  additionalInfo = '',
  number,
  linkGuide = DEFAULT_LINK_GUIDE,
  className = '',
  kinerja,
  originalKinerja,
  originalKinerjaNumber,
  kinerjaNumber,
  showChange = false,
  showDetails = true,
  hideAdjustmentLabel = false,
  label = 'Rencana Hasil Kerja',

  originalAdjustmentLabel = 'Perencanaan Guru',
  reviewAdjustmentLabel = 'Penyesuaian dari Kepala Sekolah',
  ...otherProps
}: PanelKinerjaProps) => {
  const displayedTitle = kinerja?.rencanaHasilKerja || '-'
  const displayedTargetKualitas = kinerja?.targetKualitas || '-'
  const displayedBuktiDukung =
    kinerja?.buktiDukung?.map((b) => b.deskripsi) ?? []
  const displayedTargetKuantitas = kinerja?.targetKuantitas || '-'
  const displayedTargetWaktu = translateTargetWaktu(kinerja?.targetWaktu)

  const hasChange = showChange && !isEqual(originalKinerja, kinerja)

  return (
    <div
      className={clsx('bg-white border border-gray-30 rounded p-6', className)}
      data-testid="panel-kinerja"
      {...otherProps}
    >
      {hasChange && (
        <AdjustmentInfo
          data-testid="adjustment-info"
          text={adjustmentText}
          label={hideAdjustmentLabel ? '' : label}
          className="mb-6"
          kinerja={kinerja}
          kinerjaNumber={kinerjaNumber || number}
          originalKinerja={originalKinerja}
          originalKinerjaNumber={originalKinerjaNumber || number}
          showDetails={showDetails}
          originalLabel={originalAdjustmentLabel}
          reviewLabel={reviewAdjustmentLabel}
        />
      )}

      <div className="flex items-start gap-5">
        {number && (
          <Text variant="heading-sm" color="primary">
            {number}.
          </Text>
        )}
        <div className="flex flex-col gap-1">
          {label && <Text color="subdued">{label}</Text>}
          <Text variant="heading-sm" className="!font-normal">
            {displayedTitle}
          </Text>
          {additionalInfo && showDetails && (
            <Text variant="heading-sm" className="!font-normal mt-6">
              {additionalInfo}
            </Text>
          )}
          {!!kinerja &&
            showDetails &&
            'catatan' in kinerja &&
            !!kinerja.catatan && (
              <Text className="mt-4">Catatan: {kinerja.catatan}</Text>
            )}
        </div>
      </div>

      {showDetails && (
        <>
          <Separator className="my-6" />
          <div className="flex flex-col gap-6">
            {displayedTargetKualitas && (
              <div className="flex flex-col gap-1">
                <Text color="subdued">Target Kualitas</Text>
                <Text>{displayedTargetKualitas}</Text>
              </div>
            )}

            <div className="flex gap-6">
              <div className="w-full flex flex-col gap-1">
                <Text color="subdued">Bukti Dukung</Text>
                {!displayedBuktiDukung.length && '-'}
                {displayedBuktiDukung.map((buktiDukung, i, allBuktiDukung) => (
                  <Text key={`buktiDukung.${i}`} className="!font-bold">
                    {allBuktiDukung.length > 1 && `${i + 1}. `}
                    {buktiDukung ?? '-'}
                  </Text>
                ))}
              </div>

              <div className="w-full flex flex-col gap-1">
                <Text color="subdued">Target Kuantitas</Text>
                <Text className="!font-bold">{displayedTargetKuantitas}</Text>
              </div>

              <div className="w-full flex flex-col gap-1">
                <Text color="subdued">Target Waktu</Text>
                <Text className="!font-bold">{displayedTargetWaktu}</Text>
              </div>

              {!!kinerja && 'bobot' in kinerja && !!kinerja.bobot && (
                <div className="w-full flex flex-col gap-1">
                  <Text color="subdued">Poin</Text>
                  <Text className="!font-bold">{kinerja.bobot}</Text>
                </div>
              )}
            </div>
          </div>
          {linkGuide && (
            <div className="mt-1">
              <Link
                target="_blank"
                href={linkGuide}
                data-testid="link-guide"
                className="font-semibold !no-underline text-[13px]"
              >
                Cek panduan
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default PanelKinerja
